import type { DiscoverParams, DiscoverSortBy, WorkMode } from "@/lib/types";

/**
 * Pure Discover filter logic: store state in, query params / view numbers out.
 * No React, no i18n, no store — so the grid, the list and the page share one
 * definition of "what the feed asks for" and it is unit-testable on its own.
 */

export type BudgetPreset = "0-50" | "50-200" | "200-500" | "500+" | "volunteer" | null;

export interface DiscoverFilters {
  skills: string[];
  categories: string[];
  budgetMin: number;
  budgetMax: number;
  locationCountry: string;
  locationCity: string;
  workMode: WorkMode | "";
}

/** Sentinel upper bound meaning "no maximum" — never sent to the service. */
export const NO_BUDGET_MAX = 100_000;

export const DEFAULT_FILTERS: DiscoverFilters = {
  skills: [],
  categories: [],
  budgetMin: 0,
  budgetMax: NO_BUDGET_MAX,
  locationCountry: "",
  locationCity: "",
  workMode: "",
};

export const BUDGET_PRESET_RANGES: Record<
  Exclude<BudgetPreset, null>,
  { min: number; max: number | undefined }
> = {
  "0-50": { min: 0, max: 50 },
  "50-200": { min: 50, max: 200 },
  "200-500": { min: 200, max: 500 },
  "500+": { min: 500, max: undefined },
  volunteer: { min: 0, max: 0 },
};

/** The filters' budget bounds for a preset; `null` clears them. */
export function budgetRangeFor(preset: BudgetPreset): {
  budgetMin: number;
  budgetMax: number;
} {
  if (preset === null) {
    return { budgetMin: 0, budgetMax: NO_BUDGET_MAX };
  }
  const range = BUDGET_PRESET_RANGES[preset];
  return { budgetMin: range.min, budgetMax: range.max ?? NO_BUDGET_MAX };
}

/**
 * Feed params for the grid/list views. Default-valued filters are omitted
 * rather than sent, so the react-query key stays stable across equivalent states.
 */
export function toDiscoverParams(
  filters: DiscoverFilters,
  sortBy: DiscoverSortBy,
  searchQuery: string,
): Omit<DiscoverParams, "userId" | "cursor"> {
  return {
    taskType: "human" as const,
    ...(filters.skills.length > 0 && { skills: filters.skills }),
    ...(filters.categories.length > 0 && { categories: filters.categories }),
    ...(filters.budgetMin > 0 && { budgetMin: filters.budgetMin }),
    ...(filters.budgetMax < NO_BUDGET_MAX && { budgetMax: filters.budgetMax }),
    ...(searchQuery && { search: searchQuery }),
    ...(filters.locationCountry && { locationCountry: filters.locationCountry }),
    ...(filters.locationCity && { locationCity: filters.locationCity }),
    ...(filters.workMode && { workMode: filters.workMode }),
    sortBy,
  };
}

/** Badge count on the Filters header: each skill, each category, one for budget. */
export function activeFilterCount(
  filters: DiscoverFilters,
  budgetPreset: BudgetPreset,
): number {
  return (
    filters.skills.length +
    filters.categories.length +
    (budgetPreset !== null ? 1 : 0) +
    (filters.locationCountry ? 1 : 0) +
    (filters.locationCity ? 1 : 0) +
    (filters.workMode ? 1 : 0)
  );
}

/**
 * Whether anything narrows the feed — the empty state then offers "clear
 * filters" instead of "nothing posted". Unlike `activeFilterCount`, this reads
 * the budget bounds themselves and counts a search query.
 */
export function hasNarrowingFilters(
  filters: DiscoverFilters,
  searchQuery: string,
): boolean {
  return (
    filters.skills.length > 0 ||
    filters.categories.length > 0 ||
    filters.budgetMin > 0 ||
    filters.budgetMax < NO_BUDGET_MAX ||
    !!filters.locationCountry ||
    !!filters.locationCity ||
    !!filters.workMode ||
    searchQuery.length > 0
  );
}

/** Sort choices offered by every Discover toolbar, in display order. */
export const SORT_OPTIONS: { value: DiscoverSortBy; labelKey: string }[] = [
  { value: "newest", labelKey: "discover.sortNewest" },
  { value: "budget_desc", labelKey: "discover.sortBudgetHigh" },
  { value: "budget_asc", labelKey: "discover.sortBudgetLow" },
  { value: "deadline_asc", labelKey: "discover.sortDeadline" },
];
